/**
 * Écoutes et apprentissages par catégorie.
 *
 * Barres GROUPÉES et non empilées : écouter et apprendre sont deux usages
 * distincts, pas deux parts d'un tout. Les mettre côte à côte laisse voir si
 * une catégorie s'écoute mais ne se travaille pas — ou l'inverse, ce qui est
 * l'information intéressante.
 *
 * Deux séries, donc légende obligatoire : l'identité ne repose jamais sur la
 * seule couleur.
 *
 * Remplace les deux camemberts du rapport PDF (un par usage) : à sept parts,
 * un camembert se lit mal, et deux camemberts côte à côte demandent au lecteur
 * de comparer des angles de mémoire.
 */

import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { CategoryItem, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const legend: readonly LegendEntry[] = [
  { label: 'Écoutes', color: COLORS.series1, shape: 'area' },
  { label: 'Apprentissages', color: COLORS.series2, shape: 'area' },
];

const columns: ReadonlyArray<DataColumn<CategoryItem>> = [
  { key: 'label', header: 'Catégorie', render: (row) => row.label },
  { key: 'plays', header: 'Écoutes', numeric: true, render: (row) => formatInteger(row.plays) },
  { key: 'learns', header: 'Apprentissages', numeric: true, render: (row) => formatInteger(row.learns) },
  { key: 'total', header: 'Total', numeric: true, render: (row) => formatInteger(row.total) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
  { key: 'users', header: 'Utilisateurs', numeric: true, render: (row) => formatInteger(row.uniqueUsers) },
];

export function CategoryChart({ data }: { data: readonly CategoryItem[] }) {
  const height = Math.max(240, data.length * 52 + 40);

  return (
    <ChartCard
      title="Catégories jouées"
      subtitle="Écoutes et apprentissages, côte à côte"
      className="chart-grid__wide"
      rows={data}
      columns={columns}
      chartHeight={height}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={data as CategoryItem[]} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={165}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar isAnimationActive={false} dataKey="plays" name="Écoutes" fill={COLORS.series1} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
          <Bar isAnimationActive={false} dataKey="learns" name="Apprentissages" fill={COLORS.series2} radius={[0, 4, 4, 0]} maxBarSize={BAR_MAX_SIZE} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
